import { useEffect, useRef, useState } from 'react';
import ErrorSessionDetailModal from './ErrorSessionDetailModal';
import UsageSessionDetailModal from './UsageSessionDetailModal';
import { ERROR_TYPE_LABELS, formatDuration, getErrorTypeLabel } from './dashboardSummaryUtils';
import styles from './DashboardCalendars.module.scss';

function getUsageLevel(minutes) {
  if (!minutes || minutes === 0) {
    return 0;
  }
  if (minutes < 30) {
    return 1;
  }
  if (minutes < 120) {
    return 2;
  }
  if (minutes < 240) {
    return 3;
  }
  return 4;
}

function getErrorLevel(minutes) {
  if (!minutes || minutes === 0) {
    return 0;
  }
  if (minutes < 30) {
    return 1;
  }
  if (minutes < 120) {
    return 2;
  }
  if (minutes < 240) {
    return 3;
  }
  return 4;
}

function generateCalendarWeeks(dailyData, getLevelFn, startDateStr, endDateStr) {
  const now = new Date();
  const rangeEnd = endDateStr ? new Date(endDateStr + 'T23:59:59Z') : now;
  const rangeStart = startDateStr ? new Date(startDateStr + 'T00:00:00Z') : rangeEnd;

  const startDate = new Date(rangeStart);
  const dayOfWeek = startDate.getUTCDay();
  startDate.setUTCDate(startDate.getUTCDate() - dayOfWeek);

  const endDate = new Date(rangeEnd);
  const endDayOfWeek = endDate.getUTCDay();
  endDate.setUTCDate(endDate.getUTCDate() + (6 - endDayOfWeek));

  const weeks = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      const year = currentDate.getUTCFullYear();
      const month = String(currentDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getUTCDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      const isInRange = currentDate >= rangeStart && currentDate <= rangeEnd && currentDate <= now;
      const minutes = isInRange ? dailyData[dateStr] || 0 : 0;

      week.push({
        date: new Date(currentDate),
        dateStr,
        isInRange,
        minutes,
        level: isInRange ? getLevelFn(minutes) : 0,
      });

      currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
}

function generateMonthLabels(weeks) {
  const months = [];
  let lastMonth = -1;

  weeks.forEach((week, weekIndex) => {
    const firstDay = week[0];
    if (firstDay.isInRange) {
      const month = firstDay.date.getUTCMonth();
      if (month !== lastMonth) {
        months.push({
          index: weekIndex,
          name: firstDay.date.toLocaleString('default', { month: 'short', timeZone: 'UTC' }),
        });
        lastMonth = month;
      }
    }
  });

  return months;
}

function DashboardCalendars({
  usageData,
  errorData,
  machines = [],
  scope = null,
  loading = false,
  dateRange = null,
}) {
  const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [modalInitialDate, setModalInitialDate] = useState(null);
  const [modalInitialErrorType, setModalInitialErrorType] = useState(null);
  const [usageSplitRatio, setUsageSplitRatio] = useState(0.5);
  const [errorSplitRatio, setErrorSplitRatio] = useState(0.5);

  const usagePanelBodyRef = useRef(null);
  const errorPanelBodyRef = useRef(null);
  const draggingRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = e => {
      if (!draggingRef.current) {
        return;
      }
      const { type, ref } = draggingRef.current;
      const rect = ref.current.getBoundingClientRect();
      const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0.15), 0.85);
      if (type === 'usage') {
        setUsageSplitRatio(ratio);
      } else {
        setErrorSplitRatio(ratio);
      }
    };
    const handleMouseUp = () => {
      draggingRef.current = null;
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleDayMouseEnter = (event, day, label) => {
    if (!day.isInRange) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      content: `${day.dateStr}: ${formatDuration(day.minutes)} ${label}`,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
    });
  };

  const handleDayMouseLeave = () => {
    setTooltip({ visible: false, content: '', x: 0, y: 0 });
  };

  const handleUsageDayClick = day => {
    if (!day.isInRange || day.minutes === 0) {
      return;
    }
    setModalInitialDate(day.dateStr);
    setUsageModalOpen(true);
  };

  const handleErrorDayClick = day => {
    if (!day.isInRange || day.minutes === 0) {
      return;
    }
    setModalInitialDate(day.dateStr);
    setModalInitialErrorType(null);
    setErrorModalOpen(true);
  };

  const handleErrorTypeClick = errorType => {
    setModalInitialDate(null);
    setModalInitialErrorType(errorType);
    setErrorModalOpen(true);
  };

  if (loading) {
    return (
      <div className={styles.calendarSection}>
        <div className={styles.loadingMessage}>Loading dashboard calendars...</div>
      </div>
    );
  }

  const now = new Date();

  // Determine calendar date range from props or fall back to 3-month default
  let calStartDate = dateRange?.startDate || null;
  let calEndDate = dateRange?.endDate || null;
  if (!calStartDate || !calEndDate) {
    const startOfWindow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 2, 1));
    calStartDate = startOfWindow.toISOString().split('T')[0];
    calEndDate = now.toISOString().split('T')[0];
  }

  const usageWeeks = usageData
    ? generateCalendarWeeks(usageData.daily_usage || {}, getUsageLevel, calStartDate, calEndDate)
    : [];
  const errorWeeks = errorData
    ? generateCalendarWeeks(errorData.daily_downtime || {}, getErrorLevel, calStartDate, calEndDate)
    : [];
  const usageMonthLabels = generateMonthLabels(usageWeeks);
  const errorMonthLabels = generateMonthLabels(errorWeeks);

  const startDateStr = calStartDate;
  const endDateStr = calEndDate;

  return (
    <div className={styles.calendarSection}>
      <div className={styles.calendarPanel}>
        <h3 className={styles.panelTitle}>
          Usage Overview
          <span className={styles.panelSubtitle}>
            {`${startDateStr} – ${endDateStr} · ${formatDuration(usageData?.total_minutes || 0)} total`}
          </span>
        </h3>
        <div className={styles.panelBody} ref={usagePanelBodyRef}>
          <div
            className={styles.sidebar}
            style={{ width: `calc(${usageSplitRatio * 100}% - 4px)`, flex: 'none' }}
          >
            <h4 className={styles.sidebarTitle}>Top 5 Machines</h4>
            <div className={`${styles.sidebarList} ${styles.compact}`}>
              {(usageData?.top_machines || []).map((machine, idx) => (
                <div key={machine.machine_id} className={styles.sidebarItem}>
                  <span className={styles.rank}>#{idx + 1}</span>
                  <div className={styles.sidebarInfo}>
                    <span className={styles.sidebarName}>{machine.nickname || machine.serial}</span>
                    {machine.nickname && (
                      <span className={styles.sidebarSerial}>{machine.serial}</span>
                    )}
                  </div>
                  <span className={styles.sidebarValue}>
                    {formatDuration(machine.total_minutes)}
                  </span>
                </div>
              ))}
              {(!usageData?.top_machines || usageData.top_machines.length === 0) && (
                <div className={styles.noData}>No usage data</div>
              )}
            </div>
          </div>
          <div
            className={styles.splitter}
            onMouseDown={() => {
              draggingRef.current = { type: 'usage', ref: usagePanelBodyRef };
            }}
          />
          <div className={styles.calendarWrapper} style={{ flex: '1 1 0', minWidth: 0 }}>
            <div className={styles.calendar}>
              <div className={styles.monthLabels}>
                {usageMonthLabels.map((month, idx) => (
                  <div
                    key={idx}
                    className={styles.monthLabel}
                    style={{ left: `${month.index * 15}px`, position: 'absolute' }}
                  >
                    {month.name}
                  </div>
                ))}
              </div>
              <div className={styles.calendarGrid}>
                <div className={styles.weekdayLabels}>
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, idx) => (
                    <div key={idx} className={styles.weekdayLabel}>
                      {d}
                    </div>
                  ))}
                </div>
                <div className={styles.weeksContainer}>
                  {usageWeeks.map((week, weekIdx) => (
                    <div key={weekIdx} className={styles.week}>
                      {week.map((day, dayIdx) => (
                        <div
                          key={dayIdx}
                          className={`${styles.day} ${day.isInRange ? styles[`usageLevel${day.level}`] : styles.empty}`}
                          onClick={() => handleUsageDayClick(day)}
                          onMouseEnter={event => handleDayMouseEnter(event, day, 'usage')}
                          onMouseLeave={handleDayMouseLeave}
                          style={day.isInRange && day.minutes > 0 ? { cursor: 'pointer' } : {}}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.calendarPanel}>
        <h3 className={styles.panelTitle}>
          Down Time Overview
          <span className={styles.panelSubtitle}>
            {`${startDateStr} – ${endDateStr} · ${formatDuration(errorData?.total_minutes || 0)} total`}
          </span>
        </h3>
        <div className={styles.panelBody} ref={errorPanelBodyRef}>
          <div
            className={styles.calendarWrapper}
            style={{ width: `calc(${errorSplitRatio * 100}% - 4px)`, flex: 'none' }}
          >
            <div className={styles.calendar}>
              <div className={styles.monthLabels}>
                {errorMonthLabels.map((month, idx) => (
                  <div
                    key={idx}
                    className={styles.monthLabel}
                    style={{ left: `${month.index * 15}px`, position: 'absolute' }}
                  >
                    {month.name}
                  </div>
                ))}
              </div>
              <div className={styles.calendarGrid}>
                <div className={styles.weekdayLabels}>
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, idx) => (
                    <div key={idx} className={styles.weekdayLabel}>
                      {d}
                    </div>
                  ))}
                </div>
                <div className={styles.weeksContainer}>
                  {errorWeeks.map((week, weekIdx) => (
                    <div key={weekIdx} className={styles.week}>
                      {week.map((day, dayIdx) => (
                        <div
                          key={dayIdx}
                          className={`${styles.day} ${day.isInRange ? styles[`errorLevel${day.level}`] : styles.empty}`}
                          onClick={() => handleErrorDayClick(day)}
                          onMouseEnter={event => handleDayMouseEnter(event, day, 'downtime')}
                          onMouseLeave={handleDayMouseLeave}
                          style={day.isInRange && day.minutes > 0 ? { cursor: 'pointer' } : {}}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div
            className={styles.splitter}
            onMouseDown={() => {
              draggingRef.current = { type: 'error', ref: errorPanelBodyRef };
            }}
          />
          <div className={styles.sidebar} style={{ flex: '1 1 0', minWidth: 0 }}>
            <div className={styles.sidebarHeader}>
              <h4 className={styles.sidebarTitle}>By Error Type</h4>
              <button
                type="button"
                className={styles.moreLink}
                onClick={() => {
                  setModalInitialDate(null);
                  setModalInitialErrorType(null);
                  setErrorModalOpen(true);
                }}
              >
                More...
              </button>
            </div>
            <div className={styles.sidebarList}>
              {(errorData?.error_type_summary || []).map(item => (
                <div
                  key={item.error_type}
                  className={`${styles.sidebarItem} ${styles.clickable}`}
                  onClick={() => handleErrorTypeClick(item.error_type)}
                >
                  <div className={styles.sidebarInfo}>
                    <span className={styles.sidebarName}>{getErrorTypeLabel(item.error_type)}</span>
                    {!ERROR_TYPE_LABELS[item.error_type] && (
                      <span className={styles.sidebarSerial}>{item.error_type}</span>
                    )}
                    {ERROR_TYPE_LABELS[item.error_type] && (
                      <span className={styles.sidebarSerial}>{item.error_type}</span>
                    )}
                  </div>
                  <span className={styles.sidebarValue}>{formatDuration(item.total_minutes)}</span>
                </div>
              ))}
              {(!errorData?.error_type_summary || errorData.error_type_summary.length === 0) && (
                <div className={styles.noData}>No error sessions</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {tooltip.visible && (
        <div className={styles.tooltip} style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}>
          {tooltip.content}
        </div>
      )}

      {errorModalOpen && (
        <ErrorSessionDetailModal
          machines={machines}
          scope={scope}
          initialDate={modalInitialDate}
          initialErrorType={modalInitialErrorType}
          onClose={() => setErrorModalOpen(false)}
        />
      )}

      {usageModalOpen && (
        <UsageSessionDetailModal
          machines={machines}
          scope={scope}
          initialDate={modalInitialDate}
          onClose={() => setUsageModalOpen(false)}
        />
      )}
    </div>
  );
}

export default DashboardCalendars;
